import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Stack,
  Paper,
  Divider,
  CircularProgress,
  Container
} from "@mui/material";
import {
  PlayCircleOutline,
  CheckCircle,
  ErrorOutline,
  VideoLibrary
} from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import useThemeColor from "../../utils/useTheme";
import { submitQuestionQA } from "../../api/userApi";
import { convertTextToOnlyLink, normalizeUrl } from "../../utils/getUrlFromText";
import {
  completeProfile,
  fetchCurrentUser
} from "../../features/auth/authSlice";

dayjs.extend(relativeTime);

const getVideoEmbedUrl = (url) => {
  if (!url) return null;

  try {
    const parsedUrl = new URL(url);

    // YouTube (watch)
    if (
      parsedUrl.hostname.includes("youtube.com") &&
      parsedUrl.searchParams.get("v")
    ) {
      const videoId = parsedUrl.searchParams.get("v");
      return `https://www.youtube.com/embed/${videoId}`;
    }

    // YouTube (shortened youtu.be)
    if (parsedUrl.hostname.includes("youtu.be")) {
      const videoId = parsedUrl.pathname.split("/")[1];
      return `https://www.youtube.com/embed/${videoId}`;
    }

    // Vimeo
    if (parsedUrl.hostname.includes("vimeo.com")) {
      const videoId = parsedUrl.pathname.split("/")[1];
      return `https://player.vimeo.com/video/${videoId}`;
    }

    // Dailymotion
    if (parsedUrl.hostname.includes("dailymotion.com")) {
      const videoId = parsedUrl.pathname.split("/video/")[1]?.split("_")[0];
      return `https://www.dailymotion.com/embed/video/${videoId}`;
    }

    // MP4 or direct video file
    const isVideoFile = /\.(mp4|webm|ogg|mov|avi|flv|wmv)$/i.test(
      parsedUrl.pathname
    );
    if (isVideoFile) {
      return url; // return raw URL to use in <video> tag
    }

    // Fallback for unknown URL types
    return url;
  } catch (err) {
    console.error("Invalid video URL:", url);
    return null;
  }
};

// ✅ Memoized Video Player
const VideoPlayer = React.memo(({ heading, title, video, styles }) => {
  const embedUrl = getVideoEmbedUrl(video.url);
  if (!video.url) return null;

  return (
    <Card
      elevation={0}
      sx={{
        width: "100%",
        borderRadius: 1,
        bgcolor: "transparent",
        ...(heading === "Last Week" && {
          mt: 3
        })
      }}
    >
      <CardContent sx={{ p: "0 !important" }}>
        <Box mb={2} display="flex" justifyContent="space-between">
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{ color: styles.modLabel, fontStyle: "bold" }}
          >
            {heading}{" "}
          </Typography>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            sx={{ color: styles.modLabel, fontStyle: "italic" }}
          >
            {title}
          </Typography>
        </Box>
        <Box
          sx={{
            position: "relative",
            paddingTop: "56.25%",
            bgcolor: "#000",
            borderRadius: 1,
            overflow: "hidden"
          }}
        >
          {embedUrl ? (
            <iframe
              key={video.url}
              src={embedUrl}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                border: "none"
              }}
              allowFullScreen
              title={title}
            />
          ) : (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column"
              }}
            >
              <VideoLibrary sx={{ fontSize: 48, color: "#999", mb: 2 }} />
              <Typography
                variant="body2"
                sx={{ color: styles.color, textAlign: "center", mb: 1 }}
              >
                Video format not supported for preview
              </Typography>
              <Button
                variant="outlined"
                href={normalizeUrl(video.url)}
                target="_blank"
                startIcon={<PlayCircleOutline />}
                sx={{ mt: 1, color: styles.color, borderColor: "#4e4e4e" }}
                size="small"
              >
                Open Video
              </Button>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            fontSize: 13,
            p: { xs: 2, sm: 2 },
            color: styles.color,
            whiteSpace: "pre-line"
          }}
        >
          {video?.comments ? convertTextToOnlyLink(video?.comments) : null}
        </Box>
      </CardContent>
    </Card>
  );
});

// ✅ Video rendering section extracted
const WeeklyVideos = ({ currentWeekVideo, lastWeekVideo, styles }) => {
  const formatRange = (dateStr) =>
    `(${dayjs(dateStr).subtract(7, "days").format("MMM DD, YYYY")} - ${dayjs(
      dateStr
    ).format("MMM DD, YYYY")})`;

  return (
    <>
      {currentWeekVideo.url && (
        <VideoPlayer
          heading={"Current Week"}
          title={formatRange(currentWeekVideo.modifiedAt)}
          video={currentWeekVideo}
          styles={styles}
        />
      )}
      {lastWeekVideo.url && (
        <VideoPlayer
          heading={"Last Week"}
          title={formatRange(lastWeekVideo.modifiedAt)}
          video={lastWeekVideo}
          styles={styles}
        />
      )}
    </>
  );
};

const UserWeeklyQA = ({ setOpenSubscriptionModal }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const styles = useThemeColor();
  const [question, setQuestion] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const currentWeekVideo = user?.adminQALink?.currentWeek || {};
  const lastWeekVideo = user?.adminQALink?.lastWeek || {};

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };
  useEffect(() => {
    if (
      !user?.isFailedPayment &&
      !(user?.isAdmin === 1 || ["Coach", "Company"].includes(user?.userType)) &&
      !user?.isSubscriptionActive &&
      (user?.isTrainingFreeTrialExpired || user?.isDiaryFreeTrialExpired)
    ) {
      setOpenSubscriptionModal(true);
    }
  }, [user]);

  const handleSubmitQuestion = async () => {
    if (isSubmitting || !question.trim()) {
      showSnackbar("Please enter your question", "error");
      return;
    }

    try {
      setIsSubmitting(true);
      await submitQuestionQA({ question: question.trim() });
      await new Promise((res) => setTimeout(res, 1500));
      setQuestion("");
      showSnackbar("Question Received! ", "success");
    } catch (err) {
      console.error(err);
      showSnackbar("Error submitting question. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    dispatch(
      completeProfile({
        formData: { checkQALinkReminder: false },
        userId: user?.userId,
        date: dayjs().format("YYYY-MM-DD")
      })
    );
    setTimeout(() => {
      dispatch(fetchCurrentUser(dayjs().format("YYYY-MM-DD")));
    }, 2000);
  }, [dispatch]);
  return (
    <Container
      sx={{
        width: { xs: "100%", md: "60%" },
        justifyContent: "left",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        margin: "0"
      }}
    >
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, bgcolor: "transparent" }}>
        <Typography
          sx={{ fontSize: "30px", color: styles.color, fontWeight: 400 }}
        >
          Weekly Q&A
        </Typography>
      </Paper>

      <Card elevation={0} sx={{ borderRadius: 1, bgcolor: "transparent" }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 }, pt: 1 }}>
          <Stack spacing={2}>
            <Typography variant="body1" sx={{ color: styles.color }}>
              Got a nutrition, general health, or fitness question? Drop it
              below. EC will get to as many as possible each video. A new video
              is released every week.
            </Typography>
            <Typography
              fontSize={"13px"}
              variant="body2"
              sx={{ color: styles.modLabel }}
            >
              Note: EC cannot answer individual medical questions.
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Enter your question "
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                  color: styles.color
                },
                "& .MuiInputBase-input::placeholder": {
                  color: styles.color,
                  opacity: 0.7
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#4e4e4e"
                }
              }}
            />

            <Box
              display="flex"
              justifyContent={{ xs: "stretch", sm: "flex-end" }}
            >
              <Button
                variant="contained"
                size="medium"
                onClick={handleSubmitQuestion}
                disabled={isSubmitting || !question.trim()}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={16} sx={{ color: styles.color }} />
                  ) : null
                }
                sx={{
                  py: 1,
                  px: 3,
                  borderRadius: 1,
                  textTransform: "none",
                  minWidth: { xs: "100%", sm: 140 },
                  bgcolor: styles.primaryColor,
                  color: styles.color,
                  "&:hover": { bgcolor: "transparent", opacity: 0.8 },
                  "&.Mui-disabled": {
                    opacity: 0.5,
                    backgroundColor: "#A7C8E9",
                    color: "#64AEDD"
                  }
                }}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", zIndex: 100000000000 }}
          icon={
            snackbar.severity === "success" ? <CheckCircle /> : <ErrorOutline />
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {currentWeekVideo?.url || lastWeekVideo?.url ? (
        <Divider sx={{ background: styles.notesColor, width: "100%", my: 2 }} />
      ) : (
        <></>
      )}
      {/* ✅ Memoized Video Section */}
      <WeeklyVideos
        currentWeekVideo={currentWeekVideo}
        lastWeekVideo={lastWeekVideo}
        styles={styles}
      />
    </Container>
  );
};

export default UserWeeklyQA;
